import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { subMonths, startOfDay, endOfDay } from 'date-fns';

interface ModuleDateRange {
  startDate: Date;
  endDate: Date;
}

interface DateRangeContextType {
  startDate: Date;
  endDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  setActiveModule: (moduleKey: string, defaultStart: Date, defaultEnd: Date) => void;
  moduleRanges: Record<string, ModuleDateRange>;
}

// Ce returnează useDateRange către consumatori (fără moduleRanges)
export interface UseDateRangeResult {
  startDate: Date;
  endDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
}

const DEFAULT_MODULE = 'default';

const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined);

export const DateRangeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeModule, setActiveModuleKey] = useState<string>(DEFAULT_MODULE);
  const [moduleRanges, setModuleRanges] = useState<Record<string, ModuleDateRange>>({
    [DEFAULT_MODULE]: {
      startDate: startOfDay(subMonths(new Date(), 6)),
      endDate: endOfDay(new Date()),
    },
  });

  const currentRange = moduleRanges[activeModule] ?? moduleRanges[DEFAULT_MODULE];

  const setActiveModule = useCallback((moduleKey: string, defaultStart: Date, defaultEnd: Date) => {
    setActiveModuleKey(moduleKey);
    setModuleRanges(prev => {
      if (prev[moduleKey]) return prev;
      return {
        ...prev,
        [moduleKey]: {
          startDate: startOfDay(defaultStart),
          endDate: endOfDay(defaultEnd),
        },
      };
    });
  }, []);

  const setStartDate = useCallback((date: Date) => {
    const newStart = startOfDay(date);
    setModuleRanges(prev => {
      const current = prev[activeModule];
      if (current && current.startDate.getTime() === newStart.getTime()) return prev;
      return {
        ...prev,
        [activeModule]: { ...current, startDate: newStart },
      };
    });
  }, [activeModule]);

  const setEndDate = useCallback((date: Date) => {
    const newEnd = endOfDay(date);
    setModuleRanges(prev => {
      const current = prev[activeModule];
      if (current && current.endDate.getTime() === newEnd.getTime()) return prev;
      return {
        ...prev,
        [activeModule]: { ...current, endDate: newEnd },
      };
    });
  }, [activeModule]);

  const contextValue = useMemo(() => ({
    startDate: currentRange.startDate,
    endDate: currentRange.endDate,
    setStartDate,
    setEndDate,
    setActiveModule,
    moduleRanges,
  }), [currentRange.startDate, currentRange.endDate, setStartDate, setEndDate, setActiveModule, moduleRanges]);

  return (
    <DateRangeContext.Provider value={contextValue}>
      {children}
    </DateRangeContext.Provider>
  );
};

export const useDateRange = (moduleKey?: string, defaultStart?: Date, defaultEnd?: Date): UseDateRangeResult => {
  const context = useContext(DateRangeContext);
  if (!context) throw new Error('useDateRange must be used within DateRangeProvider');

  const { setActiveModule, moduleRanges } = context;
  const defaultsRef = useRef({ defaultStart, defaultEnd });

  useLayoutEffect(() => {
    if (moduleKey && defaultsRef.current.defaultStart && defaultsRef.current.defaultEnd) {
      setActiveModule(moduleKey, defaultsRef.current.defaultStart, defaultsRef.current.defaultEnd);
    }
  }, [moduleKey, setActiveModule]);

  // Dacă avem moduleKey, citim direct din moduleRanges — NU așteptăm activeModule să se schimbe
  if (moduleKey) {
    const moduleRange = moduleRanges[moduleKey];
    return {
      startDate: moduleRange?.startDate ?? startOfDay(defaultsRef.current.defaultStart!),
      endDate: moduleRange?.endDate ?? endOfDay(defaultsRef.current.defaultEnd!),
      setStartDate: context.setStartDate,
      setEndDate: context.setEndDate,
    };
  }

  // Fără moduleKey (ex: BreadCrumb) — returnăm datele modulului activ
  return {
    startDate: context.startDate,
    endDate: context.endDate,
    setStartDate: context.setStartDate,
    setEndDate: context.setEndDate,
  };
};
